import { Component, Element, h, Host, Prop } from '@stencil/core';
import { Language, LANGUAGES, PRICING_ALIGN, PRICING_TYPES, PricingAlign, PricingType } from './models/pricing.types';

@Component({
  tag: 'wink-pricing',
  styleUrl: 'styles/pricing.default.scss',
  shadow: true,
})
export class Pricing {
  @Element() host!: HTMLWinkPricingElement;

  @Prop() price = '';
  @Prop() priceStrikethrough?: string = '';
  @Prop() startingFrom?: string = '';
  @Prop() frequency?: string = '';
  @Prop() duration?: string = '';
  @Prop() appearance: PricingType = PRICING_TYPES.text;
  @Prop() annotation: Language = LANGUAGES.nl;
  @Prop() highlightedPrice?: boolean = false;
  @Prop({ mutable: true }) alignment: PricingAlign = PRICING_ALIGN.left;

  private readonly normalText = '_normal';
  private readonly strikeThroughText = '_strikethrough';

  private readonly screenReaderAnnotations = new Map([
    [LANGUAGES.nl + this.strikeThroughText, 'Normale prijs'],
    [LANGUAGES.fr + this.strikeThroughText, 'Prix normal'],
    [LANGUAGES.en + this.strikeThroughText, 'Normal price'],
    [LANGUAGES.nl + this.normalText, 'Huidige prijs'],
    [LANGUAGES.fr + this.normalText, 'Prix Maintenant'],
    [LANGUAGES.en + this.normalText, 'Current price'],
  ]);

  private readonly pricingChild = (price: string, lang: Language, wrapperClassName: string) => {
    const currencyAfter = lang === LANGUAGES.fr ? 'currency-after' : '';
    const langDecimalPoint = lang !== LANGUAGES.en ? ',' : '.';
    const inputPrice = price ? price.replace(',', '.') : '';
    const integerPart = inputPrice.split('.')[0];
    const rawFractionalPart = inputPrice.split('.')[1];
    const fractionalPart = rawFractionalPart || '';
    const screenReaderText =
      wrapperClassName === 'pricing__promo-price'
        ? this.screenReaderAnnotations.get(lang + this.strikeThroughText)
        : this.screenReaderAnnotations.get(lang + this.normalText);

    let formatPriceHtml = (
      <div class={`${wrapperClassName} ${currencyAfter}`}>
        <span class="sr-only">{screenReaderText}</span>
        <i class="price__price-currency">€</i>
        <span class="price__price-value">{price}</span>
      </div>
    );
    if (inputPrice.includes('.') && fractionalPart) {
      formatPriceHtml = (
        <div class={`${wrapperClassName} ${currencyAfter}`}>
          <span class="sr-only">{screenReaderText}</span>
          <i class="price__price-currency">€</i>
          <span class="price__price-value">
            {integerPart}
            <span class="price__price-decimal">{langDecimalPoint + String(fractionalPart)}</span>
          </span>
        </div>
      );
    }
    return formatPriceHtml;
  };

  private readonly alignmentClass = (alignment: PricingAlign) => {
    return this.alignment !== PRICING_ALIGN.left ? `price-align__${alignment}` : '';
  };

  componentWillRender(): void {
    if (this.appearance === PRICING_TYPES.patch) {
      this.alignment = PRICING_ALIGN.center;
    }
  }

  render() {
    return (
      <Host
        type="product price"
        class={{
          'backdrop-lg': this.appearance === PRICING_TYPES.patch,
        }}>
        <div class="pricing__top">
          {(this.startingFrom || this.priceStrikethrough) && (
            <div class={`pricing__header ${this.alignmentClass(this.alignment)}`}>
              {this.startingFrom && <div class="pricing__starting-from">{this.startingFrom} </div>}
              {this.priceStrikethrough && this.pricingChild(this.priceStrikethrough, this.annotation, 'pricing__promo-price')}
            </div>
          )}

          {(this.price || this.frequency) && (
            <div class={`pricing__current-price-wrapper ${this.alignmentClass(this.alignment)}`}>
              {this.price &&
                this.pricingChild(
                  this.price,
                  this.annotation,
                  `pricing__price-value-wrapper ${this.highlightedPrice ? 'highlighted-price' : ''}  `
                )}
              {this.frequency && <div class="pricing__price-frequency">{this.frequency}</div>}
            </div>
          )}
        </div>
        {this.duration && (
          <div class={`pricing__footer ${this.alignmentClass(this.alignment)}`}>
            <div class="pricing__price-promo-duration">{this.duration}</div>
          </div>
        )}
      </Host>
    );
  }
}
